import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Link as LinkIcon, Copy, Loader2, Plus, ExternalLink, Trash2, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPaymentLinks() {
  const [links, setLinks] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Custom order form state
  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState({
    title: '',
    description: '',
    amount: '',
    buyer_can_edit: false,
    minimum_amount: '1',
    note_for_buyer: '',
  });
  const [customBusy, setCustomBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, p, e] = await Promise.all([
        axios.get(`${API_URL}/api/admin/payments/payment-links`, { withCredentials: true }),
        axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
        axios.get(`${API_URL}/api/events`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLinks(l.data || []);
      setPrograms(p.data || []);
      setEvents(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async (item_type, item_id) => {
    setBusy(`${item_type}:${item_id}`);
    try {
      await axios.post(`${API_URL}/api/admin/payments/payment-link`, { item_type, item_id }, { withCredentials: true });
      toast.success('Payment link generated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate link');
    } finally {
      setBusy(null);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  const deactivate = async (pid) => {
    if (!window.confirm('Deactivate this payment link? It will stop working immediately.')) return;
    setBusy(pid);
    try {
      await axios.delete(`${API_URL}/api/admin/payments/payment-link/${pid}`, { withCredentials: true });
      toast.success('Deactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not deactivate');
    } finally {
      setBusy(null);
    }
  };

  const createCustom = async (e) => {
    e.preventDefault();
    const title = customForm.title.trim();
    const amount = parseFloat(customForm.amount || '0');
    if (!title) { toast.error('Title is required'); return; }
    if (!amount || amount <= 0) { toast.error('Amount must be greater than 0'); return; }
    setCustomBusy(true);
    try {
      const payload = {
        title,
        description: customForm.description.trim() || null,
        amount,
        buyer_can_edit: !!customForm.buyer_can_edit,
        minimum_amount: parseFloat(customForm.minimum_amount || '1') || 1,
        note_for_buyer: customForm.note_for_buyer.trim() || null,
      };
      const r = await axios.post(`${API_URL}/api/admin/payments/custom-link`, payload, { withCredentials: true });
      toast.success('Custom payment link created');
      // Auto-copy the new link
      try { await navigator.clipboard.writeText(r.data.url); toast.success('Link copied to clipboard'); } catch { /* ignore */ }
      setCustomForm({ title: '', description: '', amount: '', buyer_can_edit: false, minimum_amount: '1', note_for_buyer: '' });
      setCustomOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create custom link');
    } finally {
      setCustomBusy(false);
    }
  };

  const isPriced = (item) => {
    const raw = String(item.price ?? '').replace('$', '').replace(',', '').replace('Free', '0').trim();
    return raw && raw !== '0';
  };

  const linkFor = (item_type, item_id) => links.find((l) => l.active && l.item_type === item_type && l.item_id === item_id);

  const priced = [
    ...programs.filter(isPriced).map((p) => ({ ...p, _type: 'program', _id: p.program_id })),
    ...events.filter(isPriced).map((e) => ({ ...e, _type: 'event', _id: e.event_id })),
  ];

  return (
    <AdminLayout title="Payment Links">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1D4ED8] text-white rounded-xl p-6 lg:p-8">
          <h1 className="text-2xl lg:text-3xl font-bold mb-2" data-testid="payment-links-title">All your Stripe Pay Links in one place</h1>
          <p className="text-white/80 text-sm max-w-2xl">
            Every paid program and event below has a one-tap shareable Stripe Payment Link. Click "Generate" once, then "Copy" any time to send the link by SMS, email, or DM. Stripe Link is automatically enabled for returning customers so they can pay with a saved card in one tap.
          </p>
        </div>

        {/* ===== Custom Order Link Generator ===== */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 lg:p-6" data-testid="custom-order-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} className="text-[#F97316]" />
                <h2 className="text-lg font-bold text-[#0A0F1C]">Custom Order Link</h2>
              </div>
              <p className="text-sm text-[#475569]">For one-off services - 90-min strategy sessions, custom coaching packages, special-priced consultations. Generate a unique Stripe checkout link in seconds and send by SMS / email / DM.</p>
            </div>
            {!customOpen && (
              <button onClick={() => setCustomOpen(true)} className="btn-primary-orange text-sm inline-flex items-center gap-1.5" data-testid="open-custom-order-btn">
                <Plus size={14} /> New Custom Order
              </button>
            )}
          </div>

          {customOpen && (
            <form onSubmit={createCustom} className="mt-5 grid gap-3 lg:grid-cols-2" data-testid="custom-order-form">
              <div className="lg:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Title (shown to buyer on Stripe checkout) *</label>
                <input type="text" required value={customForm.title} onChange={(e) => setCustomForm({ ...customForm, title: e.target.value })}
                  placeholder="e.g. 90-Minute Business Strategy Session"
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="custom-title" />
              </div>

              <div className="lg:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Description (optional sub-line)</label>
                <input type="text" value={customForm.description} onChange={(e) => setCustomForm({ ...customForm, description: e.target.value })}
                  placeholder="e.g. Includes pre-session intake form + 7-day Voxer follow-up"
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="custom-desc" />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Amount (USD) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-[#94A3B8]">$</span>
                  <input type="number" required min="0.50" step="0.01" value={customForm.amount} onChange={(e) => setCustomForm({ ...customForm, amount: e.target.value })}
                    placeholder="250.00"
                    className="w-full pl-7 pr-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="custom-amount" />
                </div>
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm pb-2">
                  <input type="checkbox" checked={customForm.buyer_can_edit} onChange={(e) => setCustomForm({ ...customForm, buyer_can_edit: e.target.checked })} data-testid="custom-buyer-edit" />
                  <span className="text-[#475569] font-bold">Let buyer change the amount</span>
                  <span className="text-[10px] text-[#94A3B8]">(Stripe shows a "Customize" field. Great for $1 test charges or pay-what-you-want.)</span>
                </label>
              </div>

              {customForm.buyer_can_edit && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Minimum amount buyer can enter</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-[#94A3B8]">$</span>
                    <input type="number" min="0.50" step="0.01" value={customForm.minimum_amount} onChange={(e) => setCustomForm({ ...customForm, minimum_amount: e.target.value })}
                      className="w-full pl-7 pr-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="custom-min" />
                  </div>
                </div>
              )}

              <div className="lg:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 block">Internal note (for your records, not shown to buyer)</label>
                <input type="text" value={customForm.note_for_buyer} onChange={(e) => setCustomForm({ ...customForm, note_for_buyer: e.target.value })}
                  placeholder="e.g. Client X agreed to $250 on 5/29 call"
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="custom-note" />
              </div>

              <div className="lg:col-span-2 flex items-center gap-2">
                <button type="submit" disabled={customBusy} className="btn-primary-orange text-sm inline-flex items-center gap-1.5" data-testid="custom-submit">
                  {customBusy ? <><Loader2 size={14} className="animate-spin" /> Creating in Stripe...</> : <><Sparkles size={14} /> Create &amp; Copy Link</>}
                </button>
                <button type="button" onClick={() => { setCustomOpen(false); setCustomForm({ title: '', description: '', amount: '', buyer_can_edit: false, minimum_amount: '1', note_for_buyer: '' }); }} className="btn-outline text-sm" data-testid="custom-cancel">Cancel</button>
              </div>
            </form>
          )}

          {/* List existing custom links */}
          {(() => {
            const customLinks = links.filter((l) => l.active && l.item_type === 'custom_order');
            if (customLinks.length === 0) return null;
            return (
              <div className="mt-5 pt-5 border-t border-[#E2E8F0] space-y-2">
                <div className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">Recent custom orders</div>
                {customLinks.slice(0, 10).map((l) => (
                  <div key={l.payment_link_id} className="flex items-center gap-2 flex-wrap text-sm" data-testid={`custom-row-${l.payment_link_id}`}>
                    <span className="font-bold text-[#0A0F1C] truncate max-w-[280px]">{l.item_name}</span>
                    <span className="text-[#F97316] font-bold">${Number(l.amount).toLocaleString()}</span>
                    {l.buyer_can_edit && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">Buyer can edit</span>}
                    <code className="bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1 rounded font-mono text-[11px] text-[#475569] truncate max-w-[280px]">{l.url}</code>
                    <button onClick={() => copy(l.url)} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`custom-copy-${l.payment_link_id}`}>
                      <Copy size={12} /> Copy
                    </button>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`custom-open-${l.payment_link_id}`}>
                      <ExternalLink size={12} /> Open
                    </a>
                    <button onClick={() => deactivate(l.payment_link_id)} disabled={busy === l.payment_link_id} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`custom-deact-${l.payment_link_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#94A3B8]"><Loader2 size={20} className="inline animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="grid gap-4">
            {priced.length === 0 && (
              <div className="text-center py-12 text-[#94A3B8]">No paid programs or events found.</div>
            )}
            {priced.map((item) => {
              const link = linkFor(item._type, item._id);
              const k = `${item._type}:${item._id}`;
              const pretty = typeof item.price === 'number' ? `$${item.price.toLocaleString()}` : item.price;
              return (
                <div key={k} className="bg-white border border-[#E2E8F0] rounded-xl p-5 lg:p-6" data-testid={`paylink-card-${k}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold text-[#0A0F1C] truncate">{item.title || item.name}</h3>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{item._type}</span>
                        <span className="text-xs font-bold text-[#F97316]">{pretty}</span>
                      </div>
                      {(item.short_description || item.description) && (
                        <p className="text-sm text-[#475569] truncate">{item.short_description || item.description}</p>
                      )}
                      {link && (
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <code className="bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1 rounded font-mono text-[#475569] truncate max-w-md">{link.url}</code>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {link ? (
                        <>
                          <button onClick={() => copy(link.url)} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid={`copy-link-${k}`}>
                            <Copy size={14} /> Copy
                          </button>
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid={`open-link-${k}`}>
                            <ExternalLink size={14} /> Open
                          </a>
                          <button onClick={() => deactivate(link.payment_link_id)} disabled={busy === link.payment_link_id} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`deact-link-${k}`}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => generate(item._type, item._id)} disabled={busy === k} className="btn-primary-blue text-sm inline-flex items-center gap-1.5" data-testid={`gen-link-${k}`}>
                          {busy === k ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Generate Pay Link
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5 text-sm text-[#475569]">
          <div className="flex items-center gap-2 font-bold text-[#0A0F1C] mb-1"><LinkIcon size={14} /> Tip</div>
          When you generate a link, Stripe creates a hosted checkout page. Customers paying with Stripe Link see their saved cards instantly. To enable Link for new customers, visit your <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] font-bold">Stripe Dashboard → Payment methods</a> and toggle Link ON.
        </div>
      </div>
    </AdminLayout>
  );
}
